import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { despesasService, Despesa, DespesaDataField, Frequencia } from '../services/despesas';
import { categoriasService, Categoria } from '../services/categorias';
import { entidadesService, Entidade } from '../services/entidades';
import { DateRangePicker, DateRange } from '../components/ui/DateRangePicker';
import { DatePicker } from '../components/ui/DatePicker';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { formatCurrency, formatDateBR } from '../utils/format';

const startOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
const endOfMonth = () => {
  const d = new Date();
  // último dia do mês: dia 0 do mês seguinte
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth()+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
};

export const ContasAPagar: React.FC = () => {
  const [items, setItems] = useState<Despesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [entidades, setEntidades] = useState<Entidade[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Por default mostramos o MÊS INTEIRO — assim contas a vencer (futuras) também
  // aparecem na listagem de "contas a pagar". Quando o user troca para "Pagamento",
  // mantém o mesmo range (mostra o que foi pago no mês todo).
  const [dateRange, setDateRange] = useState<DateRange>({
    start: startOfMonth(),
    end: endOfMonth()
  });
  const [dataField, setDataField] = useState<DespesaDataField>('vencimento');
  const [filters, setFilters] = useState({
    categoria_id: '',
    status: ''
  });

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data_vencimento: new Date().toISOString().substring(0, 10),
    entidade_id: '',
    categoria_id: '',
    status: 'pendente' as 'pendente' | 'pago' | 'vencido',
    is_recorrente: false,
    frequencia: 'mensal' as Frequencia,
  });
  const [actionFeedback, setActionFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [quickPaymentDialog, setQuickPaymentDialog] = useState<Despesa | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const showActionFeedback = (message: string, type: 'success' | 'error' = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => setActionFeedback(null), 2500);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let data = await despesasService.list({
        data_inicio: dateRange.start,
        data_fim: dateRange.end,
        data_field: dataField,
        // Permite ver cancelados quando o user explicitamente filtrar por esse status
        incluir_cancelado: filters.status === 'cancelado',
      });

      if (filters.categoria_id) {
        data = data.filter((d: Despesa) => d.categoria_id === Number(filters.categoria_id));
      }
      if (filters.status) {
        data = data.filter((d: Despesa) => d.status === filters.status);
      }

      setItems(data);
    } catch (error) {
      console.error('Erro ao buscar despesas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      const [cats, ents] = await Promise.all([
        categoriasService.list('despesa'),
        entidadesService.list('fornecedor')
      ]);
      setCategorias(cats);
      setEntidades(ents);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dateRange, dataField]);

  const openEditModal = (item: Despesa) => {
    setEditingId(item.id);
    setFormData({
      descricao: item.descricao,
      valor: item.valor_pagar.toString().replace('.', ','),
      data_vencimento: item.data_vencimento,
      entidade_id: item.entidade_id?.toString() || '',
      categoria_id: item.categoria_id?.toString() || '',
      status: item.status as any,
      is_recorrente: item.is_recorrente || false,
      frequencia: (item.frequencia as Frequencia) || 'mensal',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      descricao: '',
      valor: '',
      data_vencimento: new Date().toISOString().substring(0, 10),
      entidade_id: '',
      categoria_id: '',
      status: 'pendente',
      is_recorrente: false,
      frequencia: 'mensal',
    });
  };

  const handleCreateOrUpdate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const valorNum = Number(formData.valor.replace(',', '.'));
    if (!formData.descricao.trim()) {
      showActionFeedback('Informe a descrição', 'error');
      return;
    }
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      showActionFeedback('Valor deve ser maior que zero', 'error');
      return;
    }

    try {
      const payload: any = {
        descricao: formData.descricao,
        valor_pagar: valorNum,
        data_vencimento: formData.data_vencimento,
        status: formData.status,
        entidade_id: formData.entidade_id ? Number(formData.entidade_id) : null,
        categoria_id: formData.categoria_id ? Number(formData.categoria_id) : null,
        is_recorrente: formData.is_recorrente,
      };
      if (formData.is_recorrente) {
        payload.frequencia = formData.frequencia;
      }

      if (editingId) {
        await despesasService.update(editingId, payload);
      } else {
        await despesasService.create(payload);
      }

      closeModal();
      fetchData();
      showActionFeedback(editingId ? 'Despesa atualizada' : 'Despesa criada');
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Erro ao salvar despesa';
      showActionFeedback(msg, 'error');
    }
  };

  /** Atalho rápido: abre o ConfirmDialog perguntando data de pagamento. */
  const openQuickPaymentDialog = (despesa: Despesa, e: React.MouseEvent) => {
    e.stopPropagation();
    if (despesa.status === 'pago') return;
    setQuickPaymentDialog(despesa);
  };

  const confirmQuickPayment = async (dataPagamento?: string) => {
    if (!quickPaymentDialog || !dataPagamento) return;
    try {
      await despesasService.marcarPago(quickPaymentDialog.id, dataPagamento);
      setQuickPaymentDialog(null);
      fetchData();
      showActionFeedback('Despesa marcada como paga');
    } catch (error: any) {
      showActionFeedback(error?.response?.data?.error || 'Erro ao marcar como paga', 'error');
    }
  };

  const handleDelete = () => {
    if (!editingId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!editingId) return;
    try {
      await despesasService.delete(editingId);
      setDeleteDialogOpen(false);
      closeModal();
      fetchData();
      showActionFeedback('Despesa excluída');
    } catch (error) {
      showActionFeedback('Erro ao excluir despesa', 'error');
    }
  };


  const valorPagar = items.reduce((sum, exp) => sum + Number(exp.valor_pagar), 0);
  const valorPago = items.filter(i => i.status === 'pago').reduce((sum, exp) => sum + Number(exp.valor_pagar), 0);

  const totals = {
    valorPagar,
    valorPago,
    saldo: valorPagar - valorPago,
  };

  return (
    <Layout pageTitle="Contas a Pagar" breadcrumb="Financeiro">
      <ConfirmDialog
        isOpen={!!quickPaymentDialog}
        title="Marcar despesa como paga"
        highlight={quickPaymentDialog?.descricao}
        description={`Confirma o pagamento desta despesa? O status mudará para "Pago" e a data informada será gravada como data de pagamento.`}
        icon="paid"
        confirmLabel="Confirmar pagamento"
        input={{
          label: 'Data de pagamento',
          type: 'date',
          defaultValue: new Date().toISOString().substring(0, 10),
          required: true,
        }}
        onCancel={() => setQuickPaymentDialog(null)}
        onConfirm={confirmQuickPayment}
      />

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Excluir despesa?"
        highlight={items.find(i => i.id === editingId)?.descricao}
        description="Esta ação não pode ser desfeita. Caso queira manter o histórico, considere marcar como cancelada."
        icon="delete"
        variant="danger"
        confirmLabel="Excluir"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />

      {actionFeedback && (
        <div className="fixed top-6 right-6 z-[9999] animate-fade-in-right">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border backdrop-blur-xl ${
            actionFeedback.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}>
            <span className="material-symbols-outlined">
              {actionFeedback.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="font-bold text-sm">{actionFeedback.message}</span>
          </div>
        </div>
      )}
      <div className="space-y-8 animate-fade-in-up">
        {/* Título e Botões de Ação */}
        <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white">Contas a Pagar</h2>
            <p className="text-slate-500 mt-1">Acompanhe suas despesas e pagamentos pendentes.</p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 text-xs font-bold text-slate-400 border border-slate-700/50 rounded-xl px-4 py-2 bg-slate-800/40 hover:bg-slate-700/80 hover:text-white transition-all duration-300">
              <span className="material-symbols-outlined text-[16px]">download</span> Exportar PDF
            </button>
            <button
              onClick={() => {
                setEditingId(null);
                setIsModalOpen(true);
              }}
              className="bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Nova Despesa
            </button>
          </div>
        </section>

        {/* Filtros em Cards Glass */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle: filtrar por Vencimento ou Pagamento */}
          <div className="flex items-center bg-slate-800/40 border border-slate-700/50 rounded-xl p-1 text-xs font-bold">
            <button
              onClick={() => setDataField('vencimento')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dataField === 'vencimento'
                ? 'bg-slate-700 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-300'}`}
              title="Mostra despesas com vencimento no período"
            >
              <span className="material-symbols-outlined text-[14px] mr-1 align-middle">event</span>
              Vencimento
            </button>
            <button
              onClick={() => setDataField('pagamento')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dataField === 'pagamento'
                ? 'bg-slate-700 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-300'}`}
              title="Mostra despesas pagas no período (apenas pagas, filtradas por data_pagamento)"
            >
              <span className="material-symbols-outlined text-[14px] mr-1 align-middle">paid</span>
              Pagamento
            </button>
          </div>

          <DateRangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range)}
          />

          <div className="relative group">
            <select
              value={filters.categoria_id}
              onChange={(e) => setFilters({ ...filters, categoria_id: e.target.value })}
              className="appearance-none bg-slate-800/40 border border-slate-700/50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition-all cursor-pointer hover:border-slate-600 w-52 text-slate-300"
            >
              <option value="">Todas as categorias</option>
              {categorias.map((cat: Categoria) => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:scale-110 group-hover:text-slate-300 transition-transform">expand_more</span>
          </div>

          <div className="relative group">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="appearance-none bg-slate-800/40 border border-slate-700/50 rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 transition-all cursor-pointer hover:border-slate-600 w-44 text-slate-300"
            >
              <option value="">Todos os status</option>
              <option value="pago">Pago</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:scale-110 group-hover:text-slate-300 transition-transform">expand_more</span>
          </div>
        </div>

        {/* Tabela Interativa Premium */}
        <section className="glass-panel rounded-2xl overflow-hidden border-slate-800/50 hover:border-slate-700/50 transition-all duration-500">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead>
                <tr className="border-b border-primary/10 bg-primary/5">
                   <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                     {dataField === 'pagamento' ? 'Data Pag.' : 'Vencimento'}
                   </th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                    {dataField === 'pagamento' ? 'Vencimento' : 'Data Pag.'}
                  </th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Descrição / Fornecedor</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Categoria</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Valor</th>
                  <th className="p-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center w-28">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/5">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-10 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Carregando...</p>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-10 text-center text-slate-500 text-sm italic">Nenhuma despesa encontrada para este período.</td>
                  </tr>
                ) : items.map((item: Despesa) => (
                  <tr
                    key={item.id}
                    onClick={() => openEditModal(item)}
                    className="hover:bg-slate-800/30 transition-all duration-300 cursor-pointer group"
                  >
                    <td className="p-4">
                      <span className={`text-sm font-bold ${item.status === 'vencido' ? 'text-slate-300' : 'text-slate-400'} group-hover:text-slate-200 transition-colors`}>
                        {dataField === 'pagamento'
                          ? formatDateBR(item.data_pagamento)
                          : formatDateBR(item.data_vencimento)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-400 hidden lg:table-cell group-hover:text-slate-300 transition-colors">
                      {dataField === 'pagamento'
                        ? formatDateBR(item.data_vencimento)
                        : formatDateBR(item.data_pagamento)}
                    </td>
                    <td className="p-4">
                      <p className="text-sm font-black text-slate-200 group-hover:text-white transition-all leading-tight line-clamp-1">
                        {item.descricao || 'Sem descrição'}
                      </p>
                      <p className="text-[10px] text-slate-500 group-hover:text-slate-400 transition-colors mt-0.5 flex items-center gap-1.5">
                        {item.entidade_nome ? (
                          <>
                            <span className="material-symbols-outlined text-[12px]">storefront</span>
                            {item.entidade_nome}
                          </>
                        ) : (
                          <span className="italic">Sem fornecedor vinculado</span>
                        )}
                      </p>
                    </td>
                    <td className="p-4 text-sm text-slate-400 hidden lg:table-cell">
                      <span className="bg-slate-800/50 border border-slate-700/50 px-2 py-1 rounded-md text-[10px]">{item.categoria_nome || '—'}</span>
                    </td>
                    <td className="p-4 text-sm font-black text-right text-rose-400/90 group-hover:text-rose-400 transition-colors">
                      - {formatCurrency(item.valor_pagar)}
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center">
                        <StatusBadge status={item.status} />
                      </div>
                    </td>
                    <td className="p-5 text-right w-20" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {item.status !== 'pago' && item.status !== 'cancelado' && (
                          <button
                            onClick={(e) => openQuickPaymentDialog(item, e)}
                            title="Marcar como paga"
                            className="p-1.5 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                          >
                            <span className="material-symbols-outlined text-[18px]">paid</span>
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(item)}
                          title="Editar"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-primary hover:bg-primary/10 transition-all"
                        >
                          <span className="material-symbols-outlined text-[18px]">edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Rodapé com totais */}
              <tfoot>
                <tr className="border-t border-slate-800/80 bg-slate-800/30 relative">
                  <td colSpan={4} className="p-5 text-xs font-bold text-slate-300 uppercase tracking-wider">
                    {dataField === 'pagamento' ? 'Total Pago no Período' : 'Total do Período'}
                  </td>
                  <td className="p-5 text-lg font-black text-right text-rose-400">- {formatCurrency(totals.valorPagar)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </div>

      {/* Modal - Nova Despesa */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background-dark/80 backdrop-blur-sm animate-fade-in-up transition-opacity"
            onClick={() => setIsModalOpen(false)}
          />

          <form
            onSubmit={(e) => { e.preventDefault(); handleCreateOrUpdate(); }}
            className="relative w-full max-w-lg glass-panel bg-background-dark/95 border border-slate-700/50 rounded-2xl shadow-2xl p-8 animate-fade-in-up"
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined text-[24px]">close</span>
            </button>

            <h3 className="text-2xl font-black mb-1 text-white">{editingId ? 'Editar Despesa' : 'Nova Despesa'}</h3>
            <p className="text-sm text-slate-500 mb-8">{editingId ? 'Atualize os dados do lançamento manual.' : 'Adicione uma nova conta a pagar ao fluxo.'}</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Descrição *</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={255}
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Salário Funcionários"
                  className="w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all placeholder:text-slate-600 text-white invalid:border-rose-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Valor Pagar (R$) *</label>
                  <input
                    type="text"
                    required
                    inputMode="decimal"
                    pattern="^\d+([.,]\d{1,2})?$"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    placeholder="0,00"
                    title="Use apenas números (ex: 1500 ou 1500,50)"
                    className="w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-lg font-bold focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all placeholder:text-slate-600 text-white invalid:border-rose-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Vencimento</label>
                  <DatePicker
                    value={formData.data_vencimento}
                    onChange={(val) => setFormData({ ...formData, data_vencimento: val })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Fornecedor</label>
                  <select
                    value={formData.entidade_id}
                    onChange={(e) => setFormData({ ...formData, entidade_id: e.target.value })}
                    className="appearance-none w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all text-slate-300 cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    {entidades.map((ent: Entidade) => <option key={ent.id} value={ent.id}>{ent.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Categoria</label>
                  <select
                    value={formData.categoria_id}
                    onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    className="appearance-none w-full bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-slate-500/50 focus:border-slate-500 outline-none transition-all text-slate-300 cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    {categorias.map((cat: Categoria) => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                <div className="flex gap-4">
                  <label className="flex-1 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      className="peer sr-only"
                      checked={formData.status === 'pendente'}
                      onChange={() => setFormData({ ...formData, status: 'pendente' })}
                    />
                    <div className="text-center py-2.5 px-4 rounded-xl border border-slate-700/50 bg-slate-800/40 peer-checked:bg-slate-700/80 peer-checked:border-slate-500 peer-checked:text-white transition-all font-bold text-sm text-slate-400 group-hover:border-slate-600">
                      Pendente
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      className="peer sr-only"
                      checked={formData.status === 'vencido'}
                      onChange={() => setFormData({ ...formData, status: 'vencido' })}
                    />
                    <div className="text-center py-2.5 px-4 rounded-xl border border-slate-700/50 bg-slate-800/40 peer-checked:bg-slate-700/80 peer-checked:border-slate-500 peer-checked:text-white transition-all font-bold text-sm text-slate-400 group-hover:border-slate-600">
                      Vencido
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      className="peer sr-only"
                      checked={formData.status === 'pago'}
                      onChange={() => setFormData({ ...formData, status: 'pago' })}
                    />
                    <div className="text-center py-2.5 px-4 rounded-xl border border-slate-700/50 bg-slate-800/40 peer-checked:bg-slate-700/80 peer-checked:border-slate-500 peer-checked:text-white transition-all font-bold text-sm text-slate-400 group-hover:border-slate-600">
                      Pago
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-2">
                <label className="flex items-center gap-3 cursor-pointer group w-fit">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={formData.is_recorrente}
                      onChange={(e) => setFormData({ ...formData, is_recorrente: e.target.checked })}
                    />
                    <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:bg-primary transition-all"></div>
                    <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full peer-checked:translate-x-5 transition-all"></div>
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider group-hover:text-slate-300 transition-colors">É recorrente?</span>
                </label>

                {formData.is_recorrente && (
                  <div className="mt-4 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30 animate-fade-in-up">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Frequência</label>
                    <div className="flex bg-slate-900/50 p-1 rounded-lg border border-slate-700/50">
                      {(['mensal', 'quinzenal', 'anual'] as Frequencia[]).map((f) => (
                        <button
                          type="button"
                          key={f}
                          onClick={() => setFormData({ ...formData, frequencia: f })}
                          className={`flex-1 py-1.5 text-[11px] font-bold uppercase rounded-md transition-all ${formData.frequencia === f ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                      ⓘ Lançamento será cadastrado uma vez. Próximas ocorrências são geradas automaticamente pelo cron <code>recurring:generate</code> 7 dias antes do vencimento.
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-4 mt-6 border-t border-slate-800/50 flex justify-between gap-3">
                <div>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold text-rose-500 hover:bg-rose-500/10 transition-all flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                      Excluir
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700/50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2.5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 hover:-translate-y-0.5 shadow-lg shadow-primary/20 transition-all"
                  >
                    {editingId ? 'Salvar Alterações' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};
